import { useState } from 'react';

import { DatePicker, Input, Label, Modal } from '@components';
import { formatISODate } from '@utils/date';

interface SelectDateInputProps {
  className?: string;
  selectedDate: string;
  handleDateSelect: (newDate?: Date) => void;
  label?: string;
  datePickerLabel?: string;
  calendarStyle?: string;
  onShownDateChange?: (date: Date) => void;
  modalTitle: string;
  modalTitleStyle?: string;
  disabled?: boolean;
  isMaxDateRequired?: boolean;
  isMinDateRequired?: boolean;
  onClearEndDate?: () => void;
  minDate?: Date;
  maxDate?: Date;
  labelStyle?: string;
  isRequired?: boolean;
  initialSelectedDate?: Date;
  inputStyle?: string;
  /** Clip label to a single line with an ellipsis; full text shows in a tooltip on hover. */
  truncateLabel?: boolean;
  labelTooltipId?: string;
}

const SelectDateInput = ({
  className,
  selectedDate,
  handleDateSelect,
  datePickerLabel,
  modalTitle,
  modalTitleStyle,
  label,
  disabled,
  isMaxDateRequired = false,
  isMinDateRequired = false,
  calendarStyle,
  minDate,
  maxDate,
  isRequired = false,
  labelStyle = '',
  onClearEndDate,
  onShownDateChange,
  initialSelectedDate = new Date(),
  inputStyle,
  truncateLabel = false,
  labelTooltipId
}: SelectDateInputProps) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const onOpenDatePicker = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDatePickerOpen(true);
  };

  const onCloseDatePicker = () => {
    setIsDatePickerOpen(false);
  };

  const onDOBSelect = (dob?: Date) => {
    handleDateSelect(dob);
    setIsDatePickerOpen(false);
  };

  return (
    <div className={className}>
      {label && (
        <Label
          label={label}
          className={labelStyle}
          isRequired={isRequired}
          truncate={truncateLabel}
          tooltipId={labelTooltipId}
        />
      )}
      <Input
        disabled={disabled}
        onClearIconAction={onClearEndDate}
        leftIconName="calendar"
        value={selectedDate ? formatISODate({ date: selectedDate }) : ''}
        placeholder={datePickerLabel}
        onClick={onOpenDatePicker}
        readOnly
        inputStyle={`text-grey ${inputStyle}`}
      />
      {isDatePickerOpen && (
        <Modal
          isOpen={isDatePickerOpen}
          isCloseIconRequired
          isClickOutsideRequired
          onCancel={onCloseDatePicker}
          className="bg-theme"
          onClick={(e) => e.stopPropagation()}
        >
          <DatePicker
            title={modalTitle}
            minDate={minDate}
            maxDate={maxDate}
            confirmButtonLabel="Apply"
            confirmButtonStyle="text-primary"
            handleConfirm={onDOBSelect}
            cancelButtonLabel="Cancel"
            handleCancel={onCloseDatePicker}
            isMinDateRequired={isMinDateRequired}
            isMaxDateRequired={isMaxDateRequired}
            selectedDate={
              selectedDate ? new Date(selectedDate) : initialSelectedDate
            }
            calendarStyle={`!bg-theme ${calendarStyle}`}
            className="rounded-lg border border-grey10"
            onShownDateChange={onShownDateChange}
            titleStyle={modalTitleStyle}
          />
        </Modal>
      )}
    </div>
  );
};

export default SelectDateInput;
